const w = (f) => `/work/${f}.webp`;

export const caseStudies = [
  {
    slug: "trepanel",
    index: "01",
    title: "Trepanel",
    kicker: "Acoustic wood panel solutions",
    year: "2024",
    disciplines: ["Interface design (GUI)", "Brand identity", "Large-format print", "In-store POS", "CGI"],
    summary:
      "A wall-panel brand taken from screen to showroom: a visual panel configurator, the Trepanel Bright light-up identity, print for the Aqua range and a modular in-store display.",
    hero: w("p01_15"),
    accent: "#B4772F",
    story: {
      stats: [
        { value: "£140M", label: "in sales within 12 months" },
        { value: "12 → 30", label: "product lines by week 28" },
        { value: "Sold out", label: "entire range by week 52" },
      ],
      paragraphs: [
        "When Walls and Floors launched Trepanel, we disrupted the market so significantly that we became industry leaders within 12 months. We ran PPC, POS and in-store POS, email and social campaigns simultaneously, alongside a wave of content creators and a full PR push.",
        "We launched with 12 lines, which grew to 30 by week 28, and by week 52 we had sold out entirely. Demand was so strong that it broke our own model, and we rebuilt it stronger from the inside out. I created the campaigns, logos, packaging, supporting imagery and roomsets for both CGI and our in-house studio to support the rollout. At every milestone, we closed the gap by reintroducing the brand through purpose and presence.",
        "Trepanel went from £0 to £140 million in 12 months, and is projected to be worth just shy of £500 million in 2026, with stores across the UK and Dubai. My remit spanned supply chain, international team collaboration, and the digital, print, photography and video output that carried it.",
      ],
    },
    flow: [
      { h: "md:h-[38vw]", items: [{ src: w("p00_1769"), span: 8 }, { src: w("p15_113"), span: 4, pos: "object-center" }] },
      {
        h: "md:h-[20vw]",
        items: [
          { src: w("p01_16"), span: 5, half: true },
          { src: w("p01_17"), span: 3, fit: "contain", half: true },
          { src: w("p01_21"), span: 4 },
        ],
      },
    ],
    chapters: [
      {
        no: "I",
        title: "A configurator for walls",
        body:
          "Shoppers pick a panel pattern, narrow it down by size, slat style, pattern and special features such as acoustic or waterproof, then see the finish on a wall inside a styled room. The same flow works on laptop and phone.",
        rows: [
          { h: "md:h-[30vw]", items: [{ src: w("p01_16"), span: 12 }] },
          {
            h: "md:h-[26vw]",
            items: [
              { src: w("p01_17"), span: 4, fit: "contain", half: true },
              { src: w("p01_19"), span: 3, fit: "contain", half: true },
              { src: w("p01_21"), span: 5 },
            ],
          },
        ],
      },
      {
        no: "II",
        title: "Trepanel Bright",
        body:
          "An identity for the light-up range, built around the line \u201cIlluminate your interior space\u201d. The wordmark sits on black or white, and the product shots show the slats with their LED lighting and controls.",
        rows: [
          {
            h: "md:h-[40vw]",
            items: [
              { src: w("p15_107"), span: 5, fit: "contain" },
              { src: w("p15_113"), span: 7 },
            ],
          },
          {
            h: "md:h-[22vw]",
            items: [
              { src: w("p15_109"), span: 6, fit: "contain", half: true },
              { src: w("p15_111"), span: 6, half: true },
            ],
          },
        ],
      },
      {
        no: "III",
        title: "Into the showroom",
        body:
          "Print for Trepanel Aqua \u2014 3D PS panels made from 90% recycled polystyrene with a lifelike wood look \u2014 alongside a modular in-store display, rendered in CGI to test the layout before it was built.",
        rows: [
          {
            h: "md:h-[30vw]",
            items: [
              { src: w("p13_81"), span: 5 },
              { src: w("p16_165"), span: 7 },
            ],
          },
          {
            h: "md:h-[20vw]",
            items: [
              { src: w("p16_161"), span: 4, half: true },
              { src: w("p16_163"), span: 4, half: true },
              { src: w("p16_167"), span: 4 },
            ],
          },
        ],
      },
    ],
  },
  {
    slug: "solihull-tiling-specialists",
    index: "02",
    title: "Solihull Tiling Specialists",
    kicker: "Brand identity & website",
    year: "2023",
    disciplines: ["Branding", "UX design", "Responsive web"],
    summary:
      "A crisp diamond \u2018S\u2019 mark and a website that leads with finished work, trust signals and an easy way to get in touch \u2014 designed for desktop, tablet and phone.",
    hero: w("p09_199"),
    accent: "#3B4A8C",
    story: {
      stats: [
        { value: "60K+", label: "combined views, 2023/24" },
        { value: "Brand ambassador", label: "Walls and Floors × Solihull Tiling Specialists" },
        { value: "Tiling Contractor", label: "of the year 2025/26" },
      ],
      paragraphs: [
        "This client was unlike any other. Known as the “Celebrity Tiler”, Reiss first gained recognition after tiling the kitchen and bathroom of TV personality Stacey Solomon. Looking to capitalise on his growing profile, he approached me to redesign his website, refine his brand strategy and strengthen his connection with his audience.",
        "The goal was to create a website that showcased his craft, built trust through customer testimonials, and reflected his growing reputation within the industry. Through a combination of strategic branding, website design and creative collaboration, we helped elevate his personal brand. During this period, he became a brand ambassador for Walls and Floors, further increasing his visibility and credibility.",
        "Today, Reiss is recognised as one of the UK’s leading tilers, earning industry awards and establishing a reputation that continues to generate new opportunities and long-term business growth.",
      ],
      link: { label: "solihulltilingspecialists.co.uk", href: "https://www.solihulltilingspecialists.co.uk" },
    },
    flow: [
      {
        h: "md:h-[34vw]",
        items: [
          { src: w("p09_199"), span: 7 },
          { src: w("p09_203"), span: 5, pos: "object-top" },
        ],
      },
      {
        h: "md:h-[20vw]",
        items: [
          { src: w("p09_205"), span: 4, half: true },
          { src: w("p09_201"), span: 4, half: true },
          { src: w("p09_202"), span: 4 },
        ],
      },
    ],
    chapters: [
      {
        no: "I",
        title: "A mark cut like a tile",
        body:
          "The logo turns the \u2018S\u2019 into a set tile on its point \u2014 simple enough for a van door, sharp enough for a phone screen.",
        rows: [
          { h: "md:h-[42vw]", items: [{ src: w("p09_199"), span: 12 }] },
        ],
      },
      {
        no: "II",
        title: "A website built on trust",
        body:
          "Wide photography of finished spaces, a strip of headline numbers, the team behind the work and a clear call to get in touch. Every section answers the same question: can I trust them with my home?",
        rows: [
          {
            h: "md:h-[36vw]",
            items: [
              { src: w("p09_202"), span: 7 },
              { src: w("p09_203"), span: 5, pos: "object-top" },
            ],
          },
        ],
      },
      {
        no: "III",
        title: "Every screen",
        body: "The layout steps down cleanly from desktop to tablet and phone, keeping the same story in a single column.",
        rows: [
          {
            h: "md:h-[28vw]",
            items: [
              { src: w("p09_205"), span: 6, half: true },
              { src: w("p09_201"), span: 6, half: true },
            ],
          },
        ],
      },
    ],
  },
  {
    slug: "rehearsal-dinner-club",
    index: "03",
    title: "The Rehearsal Dinner Club",
    kicker: "Event branding & social campaign",
    year: "2025",
    disciplines: ["Event branding", "Social design", "Video", "Partnerships"],
    summary:
      "A New Year\u2019s Eve dinner club in Leicester \u2014 event identity, posters and a social campaign presented by Curated Chaos.",
    hero: w("rdc_hero"),
    accent: "#E8D21D",
    story: {
      stats: [
        { value: "164K", label: "Instagram views in 30 days (28 Nov \u2013 27 Dec)" },
        { value: "87.8K", label: "accounts reached by the ad campaign" },
        { value: "29.7K", label: "ad engagements \u00b7 3,622 link clicks" },
      ],
      paragraphs: [
        "Working with industry specialists and making it a night to remember. I partnered with leading brands and artists and YUAF to bring this evening full of celebration.",
        "The NYE Rehearsal Dinner Club \u2014 Empire Hall, Leicester, 31 December 2025 \u2014 presented by Curated Chaos in collaboration with Book Your Babysitter. A 3-course meal, drinks packages and live entertainment, with headline act Panjabi Hit Squad featuring DJ Tak, Sura and Zeta Sky.",
        "I ran the event campaign end to end: the identity, posters, flyers, stories, reels and paid social. Over 60 days the ads delivered 153,940 views and 87,871 accounts reached.",
      ],
      partners: [
        { name: "Johal", src: w("rdc_logo_johal") },
        { name: "Bhangra Blends", src: w("rdc_logo_bhangra") },
        { name: "Skiddle", src: w("rdc_logo_skiddle") },
        { name: "YUAF", src: w("rdc_logo_yuaf") },
      ],
    },
    flow: [
      {
        h: "md:h-[36vw]",
        items: [
          { src: w("rdc_yellow"), span: 4, fit: "contain", half: true },
          { src: w("rdc_babysitter"), span: 4, fit: "contain", half: true },
          { src: w("rdc_mission"), span: 4, fit: "contain" },
        ],
      },
      {
        h: "md:h-[26vw]",
        items: [
          { src: w("rdc_grid"), span: 4, fit: "contain" },
          { src: w("rdc_reel1"), span: 4, fit: "contain", half: true },
          { src: w("rdc_reel2"), span: 4, fit: "contain", half: true },
        ],
      },
    ],
    chapters: [
      {
        no: "I",
        title: "A poster you can hear",
        body:
          "Loud yellow, black and white, hand-placed stickers and a mission written to be read out loud: encouraging people to get out, tackle loneliness and empower young minds through connection.",
        rows: [
          {
            h: "md:h-[42vw]",
            items: [
              { src: w("rdc_yellow"), span: 4, fit: "contain", half: true },
              { src: w("rdc_babysitter"), span: 4, fit: "contain", half: true },
              { src: w("rdc_mission"), span: 4, fit: "contain" },
            ],
          },
        ],
      },
      {
        no: "II",
        title: "Built for the feed",
        body:
          "Teasers, reels and a consistent Instagram grid carried the build-up to the night \u2014 from the first \u201cCurated Chaos presents\u201d to the venue reveal.",
        rows: [
          {
            h: "md:h-[34vw]",
            items: [
              { src: w("rdc_grid"), span: 4, fit: "contain" },
              { src: w("rdc_reel1"), span: 4, fit: "contain", half: true },
              { src: w("rdc_reel2"), span: 4, fit: "contain", half: true },
            ],
          },
        ],
      },
      {
        no: "III",
        title: "The event campaign",
        body:
          "The headline flyer led the paid campaign, with Panjabi Hit Squad story teasers, a digital-screen placement in the city and a steady drumbeat of posts right up to the night.",
        rows: [
          {
            h: "md:h-[46vw]",
            items: [
              { src: w("rdc_flyer"), span: 4, pos: "object-top" },
              { src: w("rdc_story1"), span: 4, pos: "object-top", half: true },
              { src: w("rdc_screen"), span: 4, half: true },
            ],
          },
          {
            h: "md:h-[40vw]",
            items: [
              { src: w("rdc_story2"), span: 4, pos: "object-top", half: true },
              { src: w("rdc_story3"), span: 4, pos: "object-top", half: true },
              { src: w("rdc_post"), span: 4, pos: "object-top" },
            ],
          },
        ],
      },
      {
        no: "IV",
        title: "The numbers",
        body:
          "164,194 views in 30 days, 153,940 ad views, 87,871 accounts reached, 29,709 engagements and 3,622 link clicks across the campaign window.",
        rows: [
          {
            h: "md:h-[42vw]",
            items: [
              { src: w("rdc_ins_views"), span: 3, fit: "contain", half: true },
              { src: w("rdc_ins_ads"), span: 3, fit: "contain", half: true },
              { src: w("rdc_ins_dash"), span: 3, fit: "contain", half: true },
              { src: w("rdc_ins_nov"), span: 3, fit: "contain", half: true },
            ],
          },
        ],
      },
    ],
  },
];

export const otherWork = [
  {
    title: "Garden CGI",
    discipline: "CGI",
    year: "2024",
    flow: [{ h: "md:h-[34vw]", items: [{ src: w("p19_12"), span: 5 }, { src: w("p19_4"), span: 7 }] }],
  },
  {
    title: "Curated Chaos",
    discipline: "Brand build, UX design, web app design, flyers & social",
    year: "2025 \u2014 2026",
    flow: [
      { h: "md:h-[42vw]", items: [{ src: w("cc_diwali"), span: 12, fit: "contain" }] },
      { h: "md:h-[34vw]", items: [{ src: w("cc_d_hero"), span: 6, pos: "object-top" }, { src: w("cc_m_hero"), span: 3, pos: "object-top", half: true }, { src: w("cc_m_s1"), span: 3, pos: "object-top", half: true }] },
      { h: "md:h-[24vw]", items: [{ src: w("cc_d_s0"), span: 6, half: true }, { src: w("cc_d_s2"), span: 6, half: true }] },
    ],
  },
  {
    title: "Spec Brochures",
    discipline: "Showerwall × Splendour · Print, illustration & diagrams",
    description:
      "Brand partnership with Showerwall and Splendour, offering solutions to house builders. All illustrations, graphics, diagrams and imagery were designed by me.",
    brochure: [w("sb_01"), w("sb_02"), w("sb_03"), w("sb_04"), w("sb_05")],
    flow: [
      {
        h: "md:h-[30vw]",
        items: [
          { src: w("sb_01"), span: 3, fit: "contain", half: true },
          { src: w("sb_02"), span: 3, fit: "contain", half: true },
          { src: w("sb_03"), span: 3, fit: "contain", half: true },
          { src: w("sb_04"), span: 3, fit: "contain", half: true },
        ],
      },
    ],
  },
  {
    title: "Large format",
    discipline: "Print design",
    year: "2024",
    flow: [{ h: "md:h-[26vw]", items: [{ src: w("p13_72"), span: 7 }, { src: w("p12_40"), span: 5 }] }],
  },
  {
    title: "Walls and Floors fleet",
    discipline: "HGV curtain design",
    year: "2023",
    flow: [{ h: "md:h-[22vw]", items: [{ src: w("p11_25"), span: 4 }, { src: w("p11_28"), span: 8, fit: "contain" }] }],
  },
  {
    title: "Retail campaigns",
    discipline: "Web graphics",
    year: "2024",
    flow: [{ h: "md:h-[20vw]", items: [{ src: w("p17_180"), span: 4, half: true }, { src: w("p18_192"), span: 3, half: true }, { src: w("p17_178"), span: 5 }] }],
  },
];

export const disciplines = [
  "Interface design",
  "UX design",
  "Web app design",
  "Brand identity",
  "Logo design",
  "Large-format print",
  "Packaging",
  "In-store POS",
  "Web graphics",
  "CGI",
  "HGV curtain design",
];

export const stats = [
  { value: "14", unit: "years", label: "across brand, social, campaigns and content" },
  { value: "27.9m", unit: "impacts", label: "across social media and OOH campaigns" },
  { value: "200", unit: "creators", label: "in the independent UK creator network built at Curated Chaos" },
];

export const experience = [
  {
    role: "Creative Lead",
    org: "Contractor",
    meta: "Contract \u00b7 United Kingdom \u00b7 Hybrid",
    period: "Sep 2026 \u2014 Present",
    points: [
      "Combining creative direction with data-driven marketing, working closely with digital and marketing teams on integrated campaigns.",
      "Strengthening brand positioning, increasing customer acquisition and delivering measurable commercial growth.",
    ],
  },
  {
    role: "Creative Marketing Lead",
    org: "Curated Chaos UK",
    meta: "Full-time \u00b7 On-site",
    period: "Mar 2026 \u2014 Oct 2026",
  },
  {
    role: "Creative Lead",
    org: "Curated Chaos UK",
    meta: "Full-time \u00b7 Leicester \u00b7 Remote",
    period: "Oct 2025 \u2014 Jul 2026",
    points: [
      "Leading a culture-led events and media platform focused on affordable experiences, honest storytelling and paid opportunities for emerging creatives.",
      "Responsible for strategy, partnerships, financial oversight, events and media growth.",
    ],
  },
  {
    role: "Marketing Support Manager",
    org: "Walls and Floors",
    meta: "Full-time \u00b7 Kettering \u00b7 On-site",
    period: "Jul 2023 \u2014 Nov 2025",
    points: [
      "Led full-scale social media and marketing campaigns from concept to execution, increasing brand visibility and audience interaction.",
      "Oversaw UX/UI initiatives and managed global teams across the UK, China and Pakistan on SEO, PPC and Google Shopping.",
      "Account Manager for key client relationships; led photography and studio output for campaigns, socials and eCommerce.",
    ],
  },
  {
    role: "Senior Graphic Designer",
    org: "Walls and Floors \u00b7 Tile Mountain Group",
    meta: "Full-time \u00b7 Kettering \u00b7 On-site",
    period: "Feb 2023 \u2014 Nov 2025",
    points: [
      "Led creative projects across digital, print, in-store and social, managing full project lifecycles from concept to delivery.",
      "Oversaw UX/UI to improve the customer journey, using data insights to optimise design performance.",
      "Delivered major in-store installations with motion graphics and branded environments; mentored junior designers.",
    ],
  },
  {
    role: "Graphic Designer / Business Development Manager",
    org: "Arch Living",
    meta: "Full-time \u00b7 Leicester",
    period: "Nov 2022 \u2014 Feb 2023",
    points: [
      "Developed brand guidelines defining tone, visual identity and design standards across digital, print and in-store.",
      "Created social content and directed branded events, aligning design with leadership and sales goals.",
    ],
  },
  {
    role: "Visual Merchandiser Executive",
    org: "Next",
    meta: "Contract \u00b7 United Kingdom",
    period: "Sep 2022 \u2014 Oct 2022",
    points: [
      "Reviewed and approved product imagery and placement across NEXT Directory print and digital.",
      "Optimised the shopping journey with buyers, photographers and marketing around key trading dates.",
    ],
  },
];

export const earlierCareer = [
  { role: "Freelance Graphic Designer", org: "50-50 Solutions", period: "2018 \u2014 2024" },
  { role: "In-house Graphic Designer", org: "Tokio Marine UK", period: "2022" },
  { role: "Mid-weight Graphic Designer", org: "Walls & Floors Ltd", period: "2021 \u2014 2022" },
  { role: "Mid-weight Graphic Designer", org: "Rave & Son", period: "2015 \u2014 2018" },
  { role: "Freelance Photographer", org: "Just Eat / Uber Eats", period: "2015 \u2014 2016" },
];

export const education = [
  { course: "BA (Hons) Visual Communication: Graphic Design", place: "University of Derby", period: "2006 \u2014 2010" },
  { course: "Help to Grow: Management and Leadership", place: "University of Leicester School of Business", period: "2026" },
  { course: "Professional development", place: "B2B Quote, Bid Proposals and Writing (2025) \u00b7 LinkedIn Learning leadership courses (2023)", period: "2023 \u2014 2025" },
];

export const testimonials = [
  {
    name: "Joshua Tang",
    role: "Business Success Manager, Dun & Bradstreet",
    context: "July 2026 \u00b7 Ravin was Joshua\u2019s client",
    pull: "For both his professional and personal attributes, Ravin is a gem to work with.",
    body: [
      "I had the pleasure of working with Ravin during a lead generation project in the spring and summer of 2026, supporting the GTM and growth of his media and creative consultancy business.",
      "Throughout the project, Ravin engaged every conversation with curiosity, an open mind, and a genuine willingness to explore new ideas.",
      "Something else that stood out to me was his sense of goodwill and integrity. Ravin appreciated and reciprocated business favours during and after our time working together, without an obligation to do so. He conducts business with honour and went the extra mile without a second glance on multiple occasions.",
      "So, if you\u2019re looking to boost your business\u2019s brand and bring it to the vanguard of prospect/customer engagement, I\u2019m very happy to recommend reaching out to him. Wishing his business every success. My door is open for us to collaborate again in the future.",
    ],
  },
  {
    name: "Susie Frost",
    role: "Email Marketing Executive",
    context: "April 2022 \u00b7 Worked with Ravin on the same team",
    pull: "From my time working with Ravin, he has always approached projects with creativity, enthusiasm and passion, going above and beyond.",
    body: [],
  },
];

export const profile = {
  name: "Ravin Patel",
  role: "Creative Lead",
  email: "ravgpatel@gmail.com",
  socials: [{ label: "LinkedIn", href: "https://www.linkedin.com/in/7188719r/" }],
};
